/**
 * Dates and ages in the reader's language and time zone.
 *
 * Every function takes an explicit `now` so tests and a long-open tab behave the
 * same way; nothing here falls back to another timestamp when one is missing.
 * A missing publication time is "Datum unbekannt", never the ingestion time.
 */

export const TIME_ZONE = 'Europe/Zurich';

const dateFmt = new Intl.DateTimeFormat('de-CH', { timeZone: TIME_ZONE, day: 'numeric', month: 'long', year: 'numeric' });
const shortDateFmt = new Intl.DateTimeFormat('de-CH', { timeZone: TIME_ZONE, weekday: 'short', day: '2-digit', month: '2-digit' });
const longDayFmt = new Intl.DateTimeFormat('de-CH', { timeZone: TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long' });
const timeFmt = new Intl.DateTimeFormat('de-CH', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit' });
const dateTimeFmt = new Intl.DateTimeFormat('de-CH', {
  timeZone: TIME_ZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const isoDayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A calendar date ("2026-09-23") is a local date, not an instant; keep it that way. */
function parseLocalDate(day: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  // Noon UTC is the same calendar day in Europe/Zurich all year round.
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
}

export function formatDateTime(value: string | null | undefined): string {
  const d = parse(value);
  return d ? dateTimeFmt.format(d) : 'Datum unbekannt';
}

export function formatTime(value: string | null | undefined): string {
  const d = parse(value);
  return d ? timeFmt.format(d) : '–';
}

export function formatDate(value: string | null | undefined): string {
  const d = parse(value);
  return d ? dateFmt.format(d) : 'Datum unbekannt';
}

/** "Mittwoch, 23. September" for a local calendar date. */
export function formatLocalDay(day: string): string {
  const d = parseLocalDate(day);
  return d ? longDayFmt.format(d) : day;
}

/** "Donnerstag" for a local calendar date. */
export function formatWeekday(day: string): string {
  const d = parseLocalDate(day);
  return d ? new Intl.DateTimeFormat('de-CH', { timeZone: TIME_ZONE, weekday: 'long' }).format(d) : day;
}

/** "Mi., 23.09.2026" for a local calendar date. */
export function formatLocalDayShortYear(day: string): string {
  const d = parseLocalDate(day);
  return d
    ? new Intl.DateTimeFormat('de-CH', { timeZone: TIME_ZONE, weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
    : day;
}

/** A range of local dates; years are shown when the range crosses one. */
export function formatLocalDayRange(from: string, to: string): string {
  if (from === to) return formatLocalDayShort(from);
  if (from.slice(0, 4) !== to.slice(0, 4)) return `${formatLocalDayShortYear(from)} – ${formatLocalDayShortYear(to)}`;
  return `${formatLocalDayShort(from)} – ${formatLocalDayShort(to)}`;
}

/** "Mi., 23.09." for a local calendar date. */
export function formatLocalDayShort(day: string): string {
  const d = parseLocalDate(day);
  return d ? shortDateFmt.format(d) : day;
}

export function localDayParts(day: string): { month: string; day: string } | null {
  const d = parseLocalDate(day);
  if (!d) return null;
  return {
    month: new Intl.DateTimeFormat('de-CH', { timeZone: TIME_ZONE, month: 'short' }).format(d).replace('.', '').toUpperCase(),
    day: String(Number(day.slice(8, 10))),
  };
}

/** Today's local date in Europe/Zurich as YYYY-MM-DD. */
export function localToday(now: Date = new Date()): string {
  return isoDayFmt.format(now);
}

export function addDays(day: string, delta: number): string {
  const d = parseLocalDate(day);
  if (!d) return day;
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/** "vor 5 Min.", "vor 3 Std.", "vor 2 Tagen", "am 4. März 2026". */
export function relativeAge(value: string | null | undefined, now: Date = new Date()): string {
  const d = parse(value);
  if (!d) return 'Datum unbekannt';
  const minutes = Math.round((now.getTime() - d.getTime()) / 60_000);
  if (minutes < -5) return formatDateTime(value);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'vor 1 Tag';
  if (days < 14) return `vor ${days} Tagen`;
  return `am ${formatDate(value)}`;
}

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}
